import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Layout, LayoutContent } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Grid } from '@astryxdesign/core/Grid'
import { Card } from '@astryxdesign/core/Card'
import { Avatar } from '@astryxdesign/core/Avatar'
import { Badge } from '@astryxdesign/core/Badge'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Divider } from '@astryxdesign/core/Divider'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { Spinner } from '@astryxdesign/core/Spinner'
import StoreBadges from '../components/StoreBadges'

// The public player profile at /profile/{username}.
//
// This screen did not come from Bulma. It carried its own 300-line <style>
// block, pulled Bebas Neue and DM Sans off Google Fonts at render time, and
// hard-coded roughly twenty hex values - #080c14 for its background, #c8f135
// for every highlight, #4b5a70 for muted text - to build one bespoke dark
// card.
//
// All of that is gone, and this screen is the reason the theme briefly had
// the wrong accent in it. Reading a palette off the one screen in the app
// with a bespoke design of its own produced a green product out of a blue
// one: the lime was this card's accent, never Padelr's. The theme is seeded
// from the brand blue now, so the highlights here are the same blue as every
// other screen rather than a colour only this one used.
//
// What changed beyond colour, and it is the point of the exercise: the screen
// no longer forces dark. It was dark for everybody regardless of their
// preference, because the colours were literals. It now follows the theme
// like every other page, light or dark.
//
// Two web fonts also stopped being fetched. They were loaded with an @import
// inside an injected <style>, which is the slowest way to request a font -
// the browser cannot even discover it until the stylesheet has parsed.
//
// Every piece of behaviour is unchanged: same endpoint, same request body,
// same private-profile fallback, same fields, same formatting, same rules
// about which blocks appear at all.
const APP_STORE_URL = "https://apps.apple.com/app/padelr/id6657992480"
const PLAY_STORE_URL = "https://play.google.com/apps/testing/com.padel.day.padelr"

// The side and handedness labels, unchanged from the markup this replaces.
function describePlay(description) {
    if (!description) return null

    let side = "Deux côtés"
    if (description.side === "USER_SIDE_RIGHT") side = "Côté droit"
    else if (description.side === "USER_SIDE_LEFT") side = "Côté gauche"

    const hand = description.left_hand === true ? "Gaucher" : "Droitier"
    return `${hand} · ${side}`
}

function Stat({ value, label }) {
    return (
        <Stack gap={0.5} hAlign="center">
            <Text type="display-3" color="accent" hasTabularNumbers>{value}</Text>
            <Text type="supporting">{label}</Text>
        </Stack>
    )
}

// "Mieux avec l'app" - shown on the profile whether or not it resolved, which
// is how it behaved before.
function AppCta() {
    return (
        <Stack gap={3} hAlign="center">
            <Text type="supporting">Mieux avec l'app</Text>
            <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
        </Stack>
    )
}

export function ProfileView({ username }) {
    const [state, setState] = useState({ loaded: false, isPublic: false, user: {} })

    const retrieveProfile = useCallback(() => {
        if (!username || username.length === 0) return

        fetch("/api/v1/user/profile", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ "username": username }),
        })
            .then(res => res.json())
            .then(
                (result) => {
                    setState({ user: result.user, loaded: true, isPublic: result.is_public })
                },
                (error) => { console.log(error) },
            )
    }, [username])

    useEffect(() => { retrieveProfile() }, [retrieveProfile])

    const { loaded, isPublic, user } = state

    // Not loaded and not public were one branch before and still are - the
    // screen cannot tell "still fetching" from "refused" without a third
    // state, and inventing one would change behaviour. The spinner only
    // distinguishes them visually while the request is in flight.
    if (!loaded || !isPublic) {
        return (
            <Layout padding={6} contentWidth={480} height="auto">
                <LayoutContent padding={6}>
                    <Stack gap={8} hAlign="center">
                        {!loaded ? (
                            <Stack gap={4} hAlign="center" paddingBlock={10}>
                                <Spinner />
                            </Stack>
                        ) : (
                            <EmptyState
                                headingLevel={1}
                                title="Profil privé"
                                description="Ce joueur n'a pas rendu son profil public."
                            />
                        )}
                        <AppCta />
                    </Stack>
                </LayoutContent>
            </Layout>
        )
    }

    const fullname = (user.full_name && user.full_name.length > 0) ? user.full_name : user.username
    const play = describePlay(user.description)
    const hasStats = user.rank && user.rank > 0
    const hasClubs = user.clubs && user.clubs.length > 0

    return (
        <Layout padding={6} contentWidth={480} height="auto">
            <LayoutContent padding={6}>
                <Stack gap={8} hAlign="center">
                    <Card width="100%">
                        <Stack gap={6}>
                            <Stack gap={3} hAlign="center">
                                <Avatar
                                    size="xl"
                                    src="/img/logo-padelr-2048.png"
                                    name={fullname}
                                />
                                <Stack gap={1} hAlign="center">
                                    <Heading level={1} justify="center">{fullname}</Heading>
                                    <Text type="supporting">@{user.username}</Text>
                                </Stack>
                                <Badge variant="info" label={`Niveau ${user.level}`} />
                            </Stack>

                            {user.is_level_certified && (
                                <>
                                    <Divider />
                                    <Stack gap={1} hAlign="center">
                                        <Text type="label">
                                            🏅 Certifié par {user.certification_coach_username}
                                        </Text>
                                        <Text type="supporting">
                                            le {moment.unix(user.certified_at).format('LL')}
                                        </Text>
                                    </Stack>
                                </>
                            )}

                            {hasStats && (
                                <>
                                    <Divider />
                                    <Grid columns={4} gap={3}>
                                        <Stat value={`#${user.rank}`} label="Rang" />
                                        <Stat value={user.num_points} label="Points" />
                                        <Stat value={user.num_tournois} label="Tournois" />
                                        <Stat value={user.avg_points} label="Moy." />
                                    </Grid>
                                </>
                            )}

                            {play && (
                                <>
                                    <Divider />
                                    <Stack direction="horizontal" hAlign="center">
                                        <Badge label={play} />
                                    </Stack>
                                </>
                            )}

                            {hasClubs && (
                                <>
                                    <Divider />
                                    <Stack gap={2} hAlign="center">
                                        <Text type="supporting">Clubs suivis</Text>
                                        <Stack direction="horizontal" gap={2} wrap="wrap" hAlign="center">
                                            {user.clubs.map(club => (
                                                <Badge key={club.id} label={club.name} />
                                            ))}
                                        </Stack>
                                    </Stack>
                                </>
                            )}

                            <Divider />
                            <Stack direction="horizontal" hAlign="center">
                                <Badge variant="success" label="Profil public" />
                            </Stack>
                        </Stack>
                    </Card>

                    <AppCta />
                </Stack>
            </LayoutContent>
        </Layout>
    )
}

export default ProfileView

ProfileView.propTypes = {
    username: PropTypes.string.isRequired,
}
